import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useTRPC } from "@/trpc";
import { useInvalidateMonitoring } from "../use-monitoring-invalidation";

type Props = {
  organizationBusinessUnitId: string;
  providerUserId: string;
  providerName: string;
  /** Rules that target the provider directly. */
  programIds: string[];
  /** Pools that keep covering the provider after the direct targets go. */
  remainingPoolNames: string[];
  onClose: () => void;
};

export function StopMonitoringDialog({
  organizationBusinessUnitId,
  providerUserId,
  providerName,
  programIds,
  remainingPoolNames,
  onClose,
}: Props) {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitoring();
  const disable = useMutation(
    trpc.psv.monitoring.disableMonitoring.mutationOptions(),
  );

  // A provider can be targeted by several rules; remove them from each.
  async function stopMonitoring() {
    try {
      for (const programId of programIds) {
        await disable.mutateAsync({
          organizationBusinessUnitId,
          programId,
          target: { providerUserId },
        });
      }
    } catch {
      // The failure is shown in the dialog from `disable.error`.
      await invalidate();
      return;
    }
    await invalidate();
    toast.success("Monitoring stopped.");
    onClose();
  }
  const staysMonitored = remainingPoolNames.length > 0;
  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Stop monitoring {providerName}?</AlertDialogTitle>
          <AlertDialogDescription>
            {staysMonitored
              ? `They'll stay monitored through ${remainingPoolNames.join(", ")}.`
              : "Monthly checks stop. Past results are kept."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {disable.isError && (
          <p role="alert" className="text-sm text-muted-foreground">
            {disable.error.message}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            disabled={disable.isPending}
            onClick={() => void stopMonitoring()}
          >
            Stop monitoring
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
